import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import type { Outfit, OutfitInput, OutfitItem, OutfitPlacement } from '../../shared/outfits';
import {
  GARMENT_CATEGORIES,
  OCCASIONS,
  SEASONS,
  type GarmentCategory,
  type Occasion,
  type Season,
  type WardrobeItem,
} from '../../shared/wardrobe';
import { createOutfit, deleteOutfit, getOutfit, listOutfits, updateOutfit } from '../lib/outfits';
import { createPlacement, movePlacement, normalizeLayers } from '../lib/outfitLayout';
import { listWardrobeItems } from '../lib/items';
import { Icon } from './Icon';

function Header({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </header>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <section className="outfit-loading" aria-live="polite">
      <span className="brand-mark brand-mark--loading">
        <Icon name="outfit" />
      </span>
      <p>{label}</p>
    </section>
  );
}

function OutfitCanvas({
  items,
  selectedId,
  onSelect,
  onPointerStart,
  onPointerMove,
  onPointerEnd,
  canvasRef,
}: {
  items: OutfitItem[];
  selectedId?: string | undefined;
  onSelect?: (id: string) => void;
  onPointerStart?: (event: ReactPointerEvent<HTMLButtonElement>, item: OutfitItem) => void;
  onPointerMove?: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  onPointerEnd?: (event: ReactPointerEvent<HTMLButtonElement>) => void;
  canvasRef?: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div aria-label="Flat-lay outfit canvas" className="outfit-canvas" ref={canvasRef}>
      <div className="outfit-canvas__texture" aria-hidden="true" />
      {items.map((item) => (
        <button
          aria-label={`${item.garment.name}${selectedId === item.garmentId ? ', selected' : ''}`}
          className={`canvas-garment${selectedId === item.garmentId ? ' is-selected' : ''}`}
          key={item.garmentId}
          style={{
            left: `${item.x * 100}%`,
            top: `${item.y * 100}%`,
            zIndex: item.layer + 1,
            transform: `translate(-50%, -50%) rotate(${item.rotation}deg) scale(${item.scale})`,
          }}
          type="button"
          onClick={() => onSelect?.(item.garmentId)}
          onPointerCancel={onPointerEnd}
          onPointerDown={(event) => onPointerStart?.(event, item)}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
        >
          <img alt="" draggable={false} src={item.garment.photoUrl} />
        </button>
      ))}
      {items.length === 0 ? (
        <div className="outfit-canvas__empty">
          <Icon name="hanger" size={38} />
          <strong>Your canvas is ready</strong>
          <span>Choose a piece from your closet to begin.</span>
        </div>
      ) : null}
    </div>
  );
}

function OutfitMiniature({ outfit }: { outfit: Outfit }) {
  return (
    <div className="outfit-miniature" aria-hidden="true">
      {outfit.items.map((item) => (
        <span
          className="outfit-miniature-piece"
          key={item.garmentId}
          style={{
            left: `${item.x * 100}%`,
            top: `${item.y * 100}%`,
            zIndex: item.layer + 1,
            transform: `translate(-50%, -50%) rotate(${item.rotation}deg) scale(${item.scale})`,
          }}
        >
          <img alt="" src={item.garment.photoUrl} />
        </span>
      ))}
    </div>
  );
}

export function OutfitsPage() {
  const [outfits, setOutfits] = useState<Outfit[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      setOutfits(await listOutfits());
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'Your outfits could not load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  async function toggleFavorite(outfit: Outfit) {
    setOutfits((current) =>
      current.map((entry) =>
        entry.id === outfit.id ? { ...entry, favorite: !entry.favorite } : entry,
      ),
    );
    try {
      const updated = await updateOutfit(outfit.id, { favorite: !outfit.favorite });
      setOutfits((current) => current.map((entry) => (entry.id === updated.id ? updated : entry)));
    } catch (caught) {
      setOutfits((current) => current.map((entry) => (entry.id === outfit.id ? outfit : entry)));
      setErrorMessage(caught instanceof Error ? caught.message : 'The favourite could not update.');
    }
  }

  return (
    <div className="page">
      <Header
        action={
          <NavLink className="button button--primary" to="/outfits/new">
            <Icon name="add" size={18} />
            Create outfit
          </NavLink>
        }
        description="Arrange pieces you own and keep every detail exactly where you placed it."
        eyebrow="Saved combinations"
        title="Outfits"
      />

      {errorMessage ? (
        <div className="inline-error" role="alert">
          <span>{errorMessage}</span>
          <button type="button" onClick={() => void load()}>
            Try again
          </button>
        </div>
      ) : null}

      {loading ? (
        <LoadingCard label="Opening your outfits…" />
      ) : outfits.length ? (
        <div className="outfit-grid">
          {outfits.map((outfit) => (
            <article className="outfit-card" key={outfit.id}>
              <NavLink aria-label={`Open ${outfit.name}`} to={`/outfits/${outfit.id}`}>
                <OutfitMiniature outfit={outfit} />
              </NavLink>
              <div className="outfit-card__copy">
                <div>
                  <p className="eyebrow">{outfit.items.length} pieces</p>
                  <h2>{outfit.name}</h2>
                  <p>{[...outfit.occasions, ...outfit.seasons].slice(0, 3).join(' · ')}</p>
                </div>
                <button
                  aria-label={
                    outfit.favorite
                      ? `Remove ${outfit.name} from favourites`
                      : `Add ${outfit.name} to favourites`
                  }
                  aria-pressed={outfit.favorite}
                  className={`favorite-button${outfit.favorite ? ' is-favorite' : ''}`}
                  type="button"
                  onClick={() => void toggleFavorite(outfit)}
                >
                  <Icon name="heart" size={19} />
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <section className="empty-state">
          <span className="empty-state__icon">
            <Icon name="outfit" size={34} />
          </span>
          <p className="eyebrow">A fresh beginning</p>
          <h2>No outfits yet</h2>
          <p>Choose pieces from your closet and arrange your first complete look.</p>
          <NavLink className="button button--primary" to="/outfits/new">
            Create your first outfit
            <Icon name="arrow" size={18} />
          </NavLink>
        </section>
      )}
    </div>
  );
}

export function OutfitStudioPage() {
  const { outfitId } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(outfitId);
  const canvasRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: string; pointerId: number; dx: number; dy: number } | null>(null);
  const [wardrobe, setWardrobe] = useState<WardrobeItem[]>([]);
  const [placements, setPlacements] = useState<OutfitPlacement[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [favorite, setFavorite] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<GarmentCategory | ''>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const [items, outfit] = await Promise.all([
          listWardrobeItems(),
          outfitId ? getOutfit(outfitId) : Promise.resolve(null),
        ]);
        if (!active) return;
        if (outfit) {
          const availableIds = new Set(items.map((item) => item.id));
          setWardrobe([
            ...items,
            ...outfit.items
              .map((item) => item.garment)
              .filter((garment) => !availableIds.has(garment.id)),
          ]);
          setName(outfit.name);
          setNotes(outfit.notes ?? '');
          setSeasons(outfit.seasons);
          setOccasions(outfit.occasions);
          setFavorite(outfit.favorite);
          setPlacements(
            outfit.items.map(({ garmentId, layer, x, y, scale, rotation }) => ({
              garmentId,
              layer,
              x,
              y,
              scale,
              rotation,
            })),
          );
          setSelectedId(outfit.items.at(-1)?.garmentId);
        } else {
          setWardrobe(items);
        }
      } catch (caught) {
        if (active) {
          setErrorMessage(caught instanceof Error ? caught.message : 'The studio could not open.');
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [outfitId]);

  const wardrobeById = useMemo(
    () => new Map(wardrobe.map((garment) => [garment.id, garment])),
    [wardrobe],
  );
  const canvasItems = useMemo(
    () =>
      normalizeLayers(placements).flatMap((placement) => {
        const garment = wardrobeById.get(placement.garmentId);
        return garment ? [{ ...placement, garment }] : [];
      }),
    [placements, wardrobeById],
  );
  const selected = placements.find((placement) => placement.garmentId === selectedId);
  const selectedGarment = selectedId ? wardrobeById.get(selectedId) : undefined;
  const filteredWardrobe = wardrobe.filter(
    (garment) =>
      (!category || garment.category === category) &&
      (!search ||
        `${garment.name} ${garment.primaryColor}`
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase())),
  );

  function addGarment(garmentId: string) {
    if (placements.some((placement) => placement.garmentId === garmentId)) {
      setSelectedId(garmentId);
      return;
    }
    const next = createPlacement(garmentId, placements.length);
    setPlacements((current) => [...current, next]);
    setSelectedId(garmentId);
  }

  function updateSelected(change: (placement: OutfitPlacement) => OutfitPlacement) {
    if (!selectedId) return;
    setPlacements((current) =>
      current.map((placement) =>
        placement.garmentId === selectedId ? change(placement) : placement,
      ),
    );
  }

  function removeSelected() {
    if (!selectedId) return;
    setPlacements((current) =>
      normalizeLayers(current.filter((placement) => placement.garmentId !== selectedId)),
    );
    setSelectedId(undefined);
  }

  function moveLayer(direction: -1 | 1) {
    if (!selected) return;
    const ordered = normalizeLayers(placements);
    const index = ordered.findIndex((placement) => placement.garmentId === selected.garmentId);
    const target = Math.max(0, Math.min(ordered.length - 1, index + direction));
    if (index === target) return;
    const currentPlacement = ordered[index];
    const targetPlacement = ordered[target];
    if (!currentPlacement || !targetPlacement) return;
    ordered[index] = targetPlacement;
    ordered[target] = currentPlacement;
    setPlacements(ordered.map((placement, layer) => ({ ...placement, layer })));
  }

  function pointerStart(event: ReactPointerEvent<HTMLButtonElement>, item: OutfitItem) {
    if (!canvasRef.current) return;
    const bounds = canvasRef.current.getBoundingClientRect();
    const pointerX = (event.clientX - bounds.left) / bounds.width;
    const pointerY = (event.clientY - bounds.top) / bounds.height;
    drag.current = {
      id: item.garmentId,
      pointerId: event.pointerId,
      dx: pointerX - item.x,
      dy: pointerY - item.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedId(item.garmentId);
    event.preventDefault();
  }

  function pointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const active = drag.current;
    const canvas = canvasRef.current;
    if (!active || !canvas || active.pointerId !== event.pointerId) return;
    const bounds = canvas.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width - active.dx));
    const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height - active.dy));
    setPlacements((current) =>
      current.map((placement) =>
        placement.garmentId === active.id ? { ...placement, x, y } : placement,
      ),
    );
  }

  function pointerEnd(event: ReactPointerEvent<HTMLButtonElement>) {
    if (drag.current?.pointerId === event.pointerId) {
      drag.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  }

  function toggleTag<T extends string>(value: T, selectedValues: T[], set: (next: T[]) => void) {
    set(
      selectedValues.includes(value)
        ? selectedValues.filter((entry) => entry !== value)
        : [...selectedValues, value],
    );
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setErrorMessage('Give this outfit a name.');
      return;
    }
    if (!placements.length) {
      setErrorMessage('Add at least one garment to the canvas.');
      return;
    }
    setSaving(true);
    setErrorMessage('');
    const input: OutfitInput = {
      name,
      notes,
      seasons,
      occasions,
      favorite,
      items: normalizeLayers(placements),
    };
    try {
      const outfit =
        editing && outfitId ? await updateOutfit(outfitId, input) : await createOutfit(input);
      await navigate(`/outfits/${outfit.id}`);
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The outfit could not save.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <LoadingCard label="Preparing your Outfit Studio…" />
      </div>
    );
  }

  return (
    <div className="page outfit-studio-page">
      <Header
        action={
          <NavLink
            className="button button--secondary"
            to={outfitId ? `/outfits/${outfitId}` : '/outfits'}
          >
            Cancel
          </NavLink>
        }
        description="Drag with touch or mouse, or use the precise controls for an exact layout."
        eyebrow={editing ? 'Edit saved look' : 'Flat-lay canvas'}
        title={editing ? 'Refine this outfit' : 'Create an outfit'}
      />

      {errorMessage ? (
        <div className="inline-error" role="alert">
          <span>{errorMessage}</span>
        </div>
      ) : null}

      {wardrobe.length === 0 ? (
        <section className="empty-state">
          <span className="empty-state__icon">
            <Icon name="closet" size={34} />
          </span>
          <p className="eyebrow">Closet first</p>
          <h2>Add a piece before building an outfit</h2>
          <p>The studio uses only garments saved privately in your real closet.</p>
          <NavLink className="button button--primary" to="/add">
            Add a garment
          </NavLink>
        </section>
      ) : (
        <form onSubmit={(event) => void save(event)}>
          <div className="studio-layout">
            <section className="studio-workspace">
              <div className="studio-section-heading">
                <div>
                  <p className="eyebrow">Canvas</p>
                  <h2>{placements.length ? `${placements.length} pieces` : 'Start your look'}</h2>
                </div>
                <span className="status-pill">Layout saves exactly</span>
              </div>
              <OutfitCanvas
                canvasRef={canvasRef}
                items={canvasItems}
                selectedId={selectedId}
                onPointerEnd={pointerEnd}
                onPointerMove={pointerMove}
                onPointerStart={pointerStart}
                onSelect={setSelectedId}
              />

              {selected && selectedGarment ? (
                <div
                  className="canvas-controls"
                  aria-label={`Controls for ${selectedGarment.name}`}
                >
                  <div className="canvas-controls__title">
                    <img alt="" src={selectedGarment.photoUrl} />
                    <span>
                      <small>Selected piece</small>
                      <strong>{selectedGarment.name}</strong>
                    </span>
                  </div>
                  <div className="position-pad" aria-label="Position controls">
                    <button
                      aria-label="Move up"
                      type="button"
                      onClick={() => updateSelected((item) => movePlacement(item, 0, -0.025))}
                    >
                      ↑
                    </button>
                    <button
                      aria-label="Move left"
                      type="button"
                      onClick={() => updateSelected((item) => movePlacement(item, -0.025, 0))}
                    >
                      ←
                    </button>
                    <button
                      aria-label="Move down"
                      type="button"
                      onClick={() => updateSelected((item) => movePlacement(item, 0, 0.025))}
                    >
                      ↓
                    </button>
                    <button
                      aria-label="Move right"
                      type="button"
                      onClick={() => updateSelected((item) => movePlacement(item, 0.025, 0))}
                    >
                      →
                    </button>
                  </div>
                  <div className="canvas-control-groups">
                    <span>
                      <small>Size</small>
                      <button
                        aria-label="Make smaller"
                        type="button"
                        onClick={() =>
                          updateSelected((item) => ({
                            ...item,
                            scale: Math.max(0.4, item.scale - 0.1),
                          }))
                        }
                      >
                        −
                      </button>
                      <button
                        aria-label="Make larger"
                        type="button"
                        onClick={() =>
                          updateSelected((item) => ({
                            ...item,
                            scale: Math.min(2.5, item.scale + 0.1),
                          }))
                        }
                      >
                        +
                      </button>
                    </span>
                    <span>
                      <small>Rotate</small>
                      <button
                        aria-label="Rotate left"
                        type="button"
                        onClick={() =>
                          updateSelected((item) => ({
                            ...item,
                            rotation: Math.max(-180, item.rotation - 5),
                          }))
                        }
                      >
                        ↶
                      </button>
                      <button
                        aria-label="Rotate right"
                        type="button"
                        onClick={() =>
                          updateSelected((item) => ({
                            ...item,
                            rotation: Math.min(180, item.rotation + 5),
                          }))
                        }
                      >
                        ↷
                      </button>
                    </span>
                    <span>
                      <small>Layer</small>
                      <button
                        aria-label="Send backward"
                        type="button"
                        onClick={() => moveLayer(-1)}
                      >
                        Back
                      </button>
                      <button aria-label="Bring forward" type="button" onClick={() => moveLayer(1)}>
                        Front
                      </button>
                    </span>
                  </div>
                  <button
                    className="text-button text-button--danger"
                    type="button"
                    onClick={removeSelected}
                  >
                    Remove from outfit
                  </button>
                </div>
              ) : (
                <p className="canvas-help">Select a garment to reveal precise editing controls.</p>
              )}
            </section>

            <aside className="wardrobe-drawer">
              <div className="studio-section-heading">
                <div>
                  <p className="eyebrow">Your closet</p>
                  <h2>Add a piece</h2>
                </div>
                <span>{wardrobe.length}</span>
              </div>
              <div className="drawer-filters">
                <label className="search-field">
                  <Icon name="search" size={18} />
                  <input
                    placeholder="Search pieces"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <select
                  aria-label="Filter closet by category"
                  value={category}
                  onChange={(event) => setCategory(event.target.value as GarmentCategory | '')}
                >
                  <option value="">All categories</option>
                  {GARMENT_CATEGORIES.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="drawer-grid">
                {filteredWardrobe.map((garment) => {
                  const added = placements.some((placement) => placement.garmentId === garment.id);
                  return (
                    <button
                      aria-pressed={added}
                      className={added ? 'is-added' : ''}
                      key={garment.id}
                      type="button"
                      onClick={() => addGarment(garment.id)}
                    >
                      <img alt="" loading="lazy" src={garment.photoUrl} />
                      <span>
                        <strong>{garment.name}</strong>
                        <small>{added ? 'On canvas' : garment.category}</small>
                      </span>
                      <b>{added ? '✓' : '+'}</b>
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>

          <section className="outfit-metadata">
            <div className="form-section-heading">
              <p className="eyebrow">Save the story</p>
              <h2>Outfit details</h2>
            </div>
            <div className="field-grid">
              <label className="field field--wide">
                <span>Name</span>
                <input
                  maxLength={100}
                  placeholder="e.g. Our favourite dinner look"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label className="field field--wide">
                <span>
                  Notes <small>optional</small>
                </span>
                <textarea
                  maxLength={1000}
                  rows={3}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
            </div>
            <fieldset className="choice-fieldset">
              <legend>Season</legend>
              <div className="choice-chips">
                {SEASONS.map((option) => (
                  <button
                    aria-pressed={seasons.includes(option)}
                    className={seasons.includes(option) ? 'is-selected' : ''}
                    key={option}
                    type="button"
                    onClick={() => toggleTag(option, seasons, setSeasons)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset className="choice-fieldset">
              <legend>Occasion</legend>
              <div className="choice-chips">
                {OCCASIONS.map((option) => (
                  <button
                    aria-pressed={occasions.includes(option)}
                    className={occasions.includes(option) ? 'is-selected' : ''}
                    key={option}
                    type="button"
                    onClick={() => toggleTag(option, occasions, setOccasions)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </fieldset>
            <label className="favorite-check">
              <input
                checked={favorite}
                type="checkbox"
                onChange={(event) => setFavorite(event.target.checked)}
              />
              <span className="card-icon card-icon--rose">
                <Icon name="heart" size={20} />
              </span>
              <span>
                <strong>Favourite outfit</strong>
                <small>Keep this look close.</small>
              </span>
            </label>
            <div className="form-actions">
              <NavLink className="button button--secondary" to="/outfits">
                Cancel
              </NavLink>
              <button className="button button--primary" disabled={saving} type="submit">
                {saving ? 'Saving layout…' : editing ? 'Save changes' : 'Save outfit'}
              </button>
            </div>
          </section>
        </form>
      )}
    </div>
  );
}

export function OutfitDetailPage() {
  const { outfitId = '' } = useParams();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState<Outfit | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      setOutfit(await getOutfit(outfitId));
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The outfit could not load.');
    } finally {
      setLoading(false);
    }
  }, [outfitId]);
  useEffect(() => void load(), [load]);

  async function toggleFavorite() {
    if (!outfit) return;
    setWorking(true);
    try {
      setOutfit(await updateOutfit(outfit.id, { favorite: !outfit.favorite }));
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The favourite could not update.');
    } finally {
      setWorking(false);
    }
  }

  async function duplicate() {
    if (!outfit) return;
    setWorking(true);
    setErrorMessage('');
    try {
      const copy = await createOutfit({
        name: `${outfit.name} copy`,
        occasions: outfit.occasions,
        seasons: outfit.seasons,
        notes: outfit.notes ?? '',
        favorite: false,
        items: outfit.items.map(({ garmentId, layer, x, y, scale, rotation }) => ({
          garmentId,
          layer,
          x,
          y,
          scale,
          rotation,
        })),
      });
      await navigate(`/outfits/${copy.id}/edit`);
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The outfit could not duplicate.');
      setWorking(false);
    }
  }

  async function remove() {
    if (!outfit) return;
    // Browser dialogs are blocked in installed/embedded app contexts, so
    // destructive actions confirm with an explicit second tap instead.
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      setErrorMessage(`Tap Delete again to permanently remove “${outfit.name}”.`);
      return;
    }
    setConfirmingDelete(false);
    setErrorMessage('');
    setWorking(true);
    try {
      await deleteOutfit(outfit.id);
      await navigate('/outfits');
    } catch (caught) {
      setErrorMessage(caught instanceof Error ? caught.message : 'The outfit could not delete.');
      setWorking(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <LoadingCard label="Opening this outfit…" />
      </div>
    );
  }
  if (!outfit) {
    return (
      <div className="page">
        <section className="empty-state">
          <span className="empty-state__icon">
            <Icon name="outfit" size={34} />
          </span>
          <h2>Outfit not found</h2>
          <p>{errorMessage || 'This saved look may have been removed.'}</p>
          <NavLink className="button button--primary" to="/outfits">
            Back to outfits
          </NavLink>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <Header
        action={
          <div className="page-header-actions">
            <button
              aria-label={outfit.favorite ? 'Remove from favourites' : 'Add to favourites'}
              aria-pressed={outfit.favorite}
              className={`button button--secondary${outfit.favorite ? ' is-favorite' : ''}`}
              disabled={working}
              type="button"
              onClick={() => void toggleFavorite()}
            >
              <Icon name="heart" size={18} />
              {outfit.favorite ? 'Favourite' : 'Favourite'}
            </button>
            <NavLink className="button button--primary" to={`/outfits/${outfit.id}/edit`}>
              Edit outfit
            </NavLink>
          </div>
        }
        description={
          [...outfit.occasions, ...outfit.seasons].join(' · ') || 'A look from your closet.'
        }
        eyebrow={`${outfit.items.length} ${outfit.items.length === 1 ? 'piece' : 'pieces'}`}
        title={outfit.name}
      />
      {errorMessage ? (
        <div className="inline-error" role="alert">
          <span>{errorMessage}</span>
        </div>
      ) : null}
      <div className="outfit-detail-layout">
        <section>
          <OutfitCanvas items={outfit.items} />
        </section>
        <aside className="outfit-detail-panel">
          <div>
            <p className="eyebrow">Pieces in this look</p>
            <h2>The outfit</h2>
          </div>
          <div className="outfit-piece-list">
            {outfit.items.map((item) => (
              <NavLink key={item.garmentId} to={`/closet/${item.garmentId}`}>
                <img alt="" src={item.garment.photoUrl} />
                <span>
                  <strong>{item.garment.name}</strong>
                  <small>
                    {item.garment.category} · {item.garment.primaryColor}
                  </small>
                </span>
                <Icon name="arrow" size={17} />
              </NavLink>
            ))}
          </div>
          {outfit.notes ? <p className="outfit-notes">{outfit.notes}</p> : null}
          <div className="outfit-detail-actions">
            <NavLink className="button button--primary" to={`/try-on?outfit=${outfit.id}`}>
              <Icon name="sparkles" size={18} />
              See it on me
            </NavLink>
            <button
              className="button button--secondary"
              disabled={working}
              type="button"
              onClick={() => void duplicate()}
            >
              Duplicate and edit
            </button>
            <button
              className="text-button text-button--danger"
              disabled={working}
              type="button"
              onClick={() => void remove()}
            >
              {confirmingDelete ? 'Tap again to confirm' : 'Delete outfit'}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
